import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import {
  Store, Euro, Package, ShoppingBag, Search, 
  ChevronDown, ChevronUp, Check, Clock, XCircle, Mail,
  Loader2, AlertCircle, Eye, Ban, CheckCircle
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PLAN_BADGES = {
  starter: { label: 'Starter', color: 'bg-slate-100 text-slate-800', price: '€149/mese' },
  business: { label: 'Business', color: 'bg-blue-100 text-blue-800', price: '€249/mese' },
  premium: { label: 'Premium', color: 'bg-amber-100 text-amber-800', price: '€399/mese' }
};

const STATUS_BADGES = {
  active: { label: 'Attivo', color: 'bg-green-100 text-green-800', icon: CheckCircle },
  pending: { label: 'In attesa', color: 'bg-yellow-100 text-yellow-800', icon: Clock },
  suspended: { label: 'Sospeso', color: 'bg-red-100 text-red-800', icon: Ban },
  trial: { label: 'Prova', color: 'bg-purple-100 text-purple-800', icon: Clock }
};

const AdminVendors = () => {
  const [vendors, setVendors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedVendor, setSelectedVendor] = useState(null);
  const [vendorProducts, setVendorProducts] = useState([]);
  const [loadingProducts, setLoadingProducts] = useState(false);
  const [stats, setStats] = useState({ total: 0, active: 0, totalProducts: 0 });

  useEffect(() => {
    fetchVendors();
  }, []);

  const fetchVendors = async () => {
    try {
      const token = localStorage.getItem('qantri_admin_token');
      const response = await axios.get(`${API}/admin/vendors`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const vendorsList = response.data.vendors || [];
      setVendors(vendorsList);
      
      // Calculate stats
      setStats({
        total: vendorsList.length,
        active: vendorsList.filter(v => v.status === 'active').length,
        totalProducts: vendorsList.reduce((sum, v) => sum + (v.products_count || 0), 0)
      });
    } catch (error) {
      toast.error('Errore nel caricamento venditori');
    } finally {
      setLoading(false);
    }
  };

  const fetchVendorProducts = async (vendorId) => {
    setLoadingProducts(true);
    try {
      const token = localStorage.getItem('qantri_admin_token');
      // Get products for this vendor
      const response = await axios.get(`${API}/products?vendor_id=${vendorId}&limit=20`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setVendorProducts(response.data.products || []);
    } catch (error) {
      console.error('Error fetching vendor products:', error);
      setVendorProducts([]);
    } finally {
      setLoadingProducts(false);
    }
  };

  const updateVendorStatus = async (vendorId, newStatus) => {
    try {
      const token = localStorage.getItem('qantri_admin_token');
      await axios.put(
        `${API}/admin/vendors/${vendorId}/status?status=${newStatus}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Stato venditore aggiornato a "${STATUS_BADGES[newStatus]?.label || newStatus}"`);
      fetchVendors();
    } catch (error) {
      toast.error('Errore nell\'aggiornamento');
    }
  };

  const selectVendor = (vendor) => {
    if (selectedVendor?.id === vendor.id) {
      setSelectedVendor(null);
      setVendorProducts([]);
    } else {
      setSelectedVendor(vendor);
      fetchVendorProducts(vendor.id);
    }
  };

  const filteredVendors = vendors.filter(v =>
    v.business_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    v.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    v.contact_name?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div data-testid="admin-vendors">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-2xl font-bold mb-2">Gestione Venditori</h1>
        <p className="text-muted-foreground">Monitora e gestisci i venditori sulla piattaforma</p>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-xl p-5 shadow-sm border"
        >
          <div className="flex items-center justify-between mb-3">
            <Store className="h-5 w-5 text-primary" />
          </div>
          <p className="text-3xl font-bold">{stats.total}</p>
          <p className="text-sm text-muted-foreground">Venditori totali</p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="bg-white rounded-xl p-5 shadow-sm border"
        >
          <div className="flex items-center justify-between mb-3">
            <CheckCircle className="h-5 w-5 text-emerald-500" />
          </div>
          <p className="text-3xl font-bold text-emerald-600">{stats.active}</p>
          <p className="text-sm text-muted-foreground">Attivi</p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="bg-white rounded-xl p-5 shadow-sm border"
        >
          <div className="flex items-center justify-between mb-3">
            <Package className="h-5 w-5 text-blue-500" />
          </div>
          <p className="text-3xl font-bold">{stats.totalProducts}</p>
          <p className="text-sm text-muted-foreground">Prodotti totali</p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="bg-white rounded-xl p-5 shadow-sm border"
        >
          <div className="flex items-center justify-between mb-3">
            <Euro className="h-5 w-5 text-amber-500" />
          </div>
          <p className="text-3xl font-bold text-amber-600">
            €{vendors.reduce((sum, v) => {
              const plan = v.plan_id || 'starter';
              const prices = { starter: 149, business: 249, premium: 399 };
              return sum + (prices[plan] || 149);
            }, 0)}/mese
          </p>
          <p className="text-sm text-muted-foreground">Entrate abbonamenti</p>
        </motion.div>
      </div>

      {/* Search */}
      <div className="bg-white rounded-xl shadow-sm border p-4 mb-6">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Cerca per nome azienda, email o contatto..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
          />
        </div>
      </div>

      {/* Vendors List */}
      <div className="bg-white rounded-xl shadow-sm border overflow-hidden">
        <div className="grid grid-cols-12 gap-4 p-4 bg-muted/50 text-sm font-medium text-muted-foreground">
          <div className="col-span-3">Venditore</div>
          <div className="col-span-2">Piano</div>
          <div className="col-span-2">Stato</div>
          <div className="col-span-2 text-right">Prodotti</div>
          <div className="col-span-2 text-right">Commissione</div>
          <div className="col-span-1"></div>
        </div>

        {filteredVendors.length === 0 ? (
          <div className="p-12 text-center">
            <Store className="h-12 w-12 text-muted-foreground/30 mx-auto mb-4" />
            <p className="text-muted-foreground">
              {vendors.length === 0 
                ? 'Nessun venditore registrato' 
                : 'Nessun venditore trovato'}
            </p>
          </div>
        ) : (
          <div className="divide-y">
            {filteredVendors.map((vendor) => {
              const plan = PLAN_BADGES[vendor.plan_id] || PLAN_BADGES.starter;
              const status = STATUS_BADGES[vendor.status] || STATUS_BADGES.pending;
              const StatusIcon = status.icon;
              const isExpanded = selectedVendor?.id === vendor.id;

              return (
                <div key={vendor.id}>
                  <div
                    className={`grid grid-cols-12 gap-4 p-4 items-center hover:bg-muted/30 cursor-pointer transition-colors ${
                      isExpanded ? 'bg-muted/50' : ''
                    }`}
                    onClick={() => selectVendor(vendor)}
                  >
                    <div className="col-span-3">
                      <p className="font-medium truncate">{vendor.business_name}</p>
                      <p className="text-sm text-muted-foreground truncate">{vendor.email}</p>
                    </div>
                    <div className="col-span-2">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${plan.color}`}>
                        {plan.label}
                      </span>
                      <p className="text-xs text-muted-foreground mt-1">{plan.price}</p>
                    </div>
                    <div className="col-span-2">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium inline-flex items-center gap-1 ${status.color}`}>
                        <StatusIcon className="h-3 w-3" />
                        {status.label}
                      </span>
                    </div>
                    <div className="col-span-2 text-right">
                      <span className="font-medium">{vendor.products_count || 0}</span>
                      <span className="text-muted-foreground text-sm">
                        /{vendor.plan_id === 'premium' ? '∞' : vendor.plan_id === 'business' ? '300' : '100'}
                      </span>
                    </div>
                    <div className="col-span-2 text-right">
                      <span className="font-medium">
                        {vendor.plan_id === 'premium' ? '10%' : vendor.plan_id === 'business' ? '15%' : '20%'}
                      </span>
                    </div>
                    <div className="col-span-1 text-right">
                      {isExpanded ? (
                        <ChevronUp className="h-5 w-5 text-muted-foreground" />
                      ) : (
                        <ChevronDown className="h-5 w-5 text-muted-foreground" />
                      )}
                    </div>
                  </div>

                  {/* Expanded Vendor Details */}
                  {isExpanded && (
                    <motion.div
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: 'auto' }}
                      exit={{ opacity: 0, height: 0 }}
                      className="bg-muted/20 border-t"
                    >
                      <div className="p-4">
                        <div className="flex items-center justify-between mb-4">
                          <div>
                            <h4 className="font-medium">Dettagli Venditore</h4>
                            <p className="text-sm text-muted-foreground">
                              Registrato il {new Date(vendor.created_at).toLocaleDateString('it-IT')}
                            </p>
                          </div>
                          <div className="flex gap-2">
                            <a
                              href={`mailto:${vendor.email}`}
                              className="text-sm text-primary hover:underline flex items-center gap-1"
                            >
                              <Mail className="h-4 w-4" />
                              Contatta
                            </a>
                          </div>
                        </div>

                        {/* Vendor Info */}
                        <div className="bg-white rounded-lg p-4 mb-4 grid grid-cols-2 md:grid-cols-4 gap-4">
                          <div>
                            <p className="text-xs text-muted-foreground">Contatto</p>
                            <p className="font-medium">{vendor.contact_name || '-'}</p>
                          </div>
                          <div>
                            <p className="text-xs text-muted-foreground">Telefono</p>
                            <p className="font-medium">{vendor.phone || '-'}</p>
                          </div>
                          <div>
                            <p className="text-xs text-muted-foreground">P.IVA</p>
                            <p className="font-medium">{vendor.vat_number || '-'}</p>
                          </div>
                          <div>
                            <p className="text-xs text-muted-foreground">Indirizzo</p>
                            <p className="font-medium truncate">{vendor.address || '-'}</p>
                          </div>
                        </div>

                        {/* Status Actions */}
                        <div className="bg-white rounded-lg p-4 mb-4">
                          <p className="text-sm font-medium mb-3">Cambia Stato</p>
                          <div className="flex gap-2">
                            <Button
                              size="sm"
                              variant={vendor.status === 'active' ? 'default' : 'outline'}
                              onClick={(e) => {
                                e.stopPropagation();
                                updateVendorStatus(vendor.id, 'active');
                              }}
                              className={vendor.status === 'active' ? 'bg-emerald-600' : ''}
                            >
                              <CheckCircle className="h-4 w-4 mr-1" />
                              Attiva
                            </Button>
                            <Button
                              size="sm"
                              variant={vendor.status === 'suspended' ? 'destructive' : 'outline'}
                              onClick={(e) => {
                                e.stopPropagation();
                                updateVendorStatus(vendor.id, 'suspended');
                              }}
                            >
                              <Ban className="h-4 w-4 mr-1" />
                              Sospendi
                            </Button>
                          </div>
                        </div>

                        {/* Products Preview */}
                        <div>
                          <p className="text-sm font-medium mb-3">Prodotti del Venditore</p>
                          {loadingProducts ? (
                            <div className="flex items-center justify-center py-8">
                              <Loader2 className="h-6 w-6 animate-spin text-primary" />
                            </div>
                          ) : vendorProducts.length === 0 ? (
                            <div className="text-center py-8 text-muted-foreground bg-white rounded-lg">
                              <Package className="h-8 w-8 mx-auto mb-2 opacity-50" />
                              <p>Nessun prodotto ancora</p>
                            </div>
                          ) : (
                            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                              {vendorProducts.slice(0, 8).map((product) => (
                                <div
                                  key={product.id}
                                  className="bg-white rounded-lg p-3 flex flex-col"
                                >
                                  <div className="aspect-square rounded-md bg-muted mb-2 overflow-hidden">
                                    <img
                                      src={product.images?.[0] || '/placeholder.png'}
                                      alt={product.name}
                                      className="w-full h-full object-cover"
                                    />
                                  </div>
                                  <p className="text-sm font-medium truncate">{product.name}</p>
                                  <p className="text-sm text-primary">€{product.price?.toFixed(2)}</p>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      </div>
                    </motion.div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminVendors;
